import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Field } from '@/components/ui/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SelectOptionWithDescription } from '@/components/ui/select-option-with-description';
import { Textarea } from '@/components/ui/textarea';
import {
  DEPARTMENT_LABEL_KEYS,
  DEPARTMENT_NONE,
} from '@constants/documentMetadata';
import type { DocumentCategoryOption } from '@services/document.service';
import { MetadataSection } from './MetadataSection';
import type { MetadataFormData } from './useDocumentMetadataForm';

interface MetadataBasicSectionProps {
  formData: MetadataFormData;
  errors: Record<string, string>;
  onChange: <K extends keyof MetadataFormData>(key: K, value: MetadataFormData[K]) => void;
  categories: DocumentCategoryOption[];
  categoriesLoading: boolean;
}

const MetadataBasicSection: React.FC<MetadataBasicSectionProps> = ({
  formData,
  errors,
  onChange,
  categories,
  categoriesLoading,
}) => {
  const { t } = useTranslation();

  return (
    <MetadataSection title={t('metadata.basicInformation')}>
      <Field label={t('metadata.category')} required error={errors.category}>
        {(control) => (
          <Select
            value={formData.category}
            onValueChange={(value) => onChange('category', value)}
            disabled={categoriesLoading}
          >
            <SelectTrigger {...control}>
              <SelectValue placeholder={t('common.select')} />
            </SelectTrigger>
            <SelectContent className="revy-ui">
              {categories.map((category) => (
                <SelectOptionWithDescription
                  key={category.code}
                  value={category.code}
                  name={category.name}
                  description={category.description ?? undefined}
                />
              ))}
              {!categoriesLoading && categories.length === 0 && (
                <SelectItem value="general">{t('documents.generalCategory')}</SelectItem>
              )}
            </SelectContent>
          </Select>
        )}
      </Field>

      <Field
        label={t('metadata.description')}
        help={errors.description || t('metadata.descriptionHelp')}
        error={errors.description}
      >
        {(control) => (
          <Textarea
            {...control}
            rows={3}
            className="revy-cjk text-sm leading-[1.8]"
            value={formData.description}
            onChange={(event) => onChange('description', event.target.value)}
          />
        )}
      </Field>

      <Field label={t('metadata.department')}>
        {(control) => (
          <Select
            value={formData.department || DEPARTMENT_NONE}
            onValueChange={(value) =>
              onChange('department', value === DEPARTMENT_NONE ? '' : value)
            }
          >
            <SelectTrigger {...control}>
              <SelectValue placeholder={t('common.select')} />
            </SelectTrigger>
            <SelectContent className="revy-ui">
              <SelectItem value={DEPARTMENT_NONE}>
                <span className="revy-cjk italic text-muted-foreground">{t('metadata.none')}</span>
              </SelectItem>
              {DEPARTMENT_LABEL_KEYS.map((key) => {
                const label = t(key);
                return (
                  <SelectItem key={key} value={label}>
                    <span className="revy-cjk">{label}</span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        )}
      </Field>
    </MetadataSection>
  );
};

export { MetadataBasicSection };
